import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRightLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/** Umschalten Milchbüchlein ↔ doppelte Buchhaltung (keine Datenmigration – nur die Oberfläche ändert sich). */
export function useAccountingModeSwitch() {
  const { company, setCompany } = useCompany();
  const [busy, setBusy] = useState(false);
  const switchMode = async (mode) => {
    if (!company?._id) return;
    setBusy(true);
    try {
      await apiClient.post(`/companies/${company._id}/accounting-mode`, { mode });
      setCompany({ ...company, accounting_mode: mode });
      toast.success(mode === "double" ? "Doppelte Buchhaltung aktiviert – alle Funktionen sind jetzt sichtbar." : "Milchbüchlein-Modus aktiviert – vereinfachte Oberfläche.");
    } catch (e) { toast.error(e.response?.data?.detail || "Modus konnte nicht gewechselt werden"); }
    finally { setBusy(false); }
  };
  return { switchMode, busy, mode: company?.accounting_mode || "double" };
}

/** Warn-Banner: Umsatzschwelle CHF 500'000 (OR 957) – ab 90 % Vorwarnung, Umstell-Button auf doppelte Buchhaltung. */
export function SimpleModeThresholdBanner({ threshold: given }) {
  const { company } = useCompany();
  const navigate = useNavigate();
  const { switchMode, busy } = useAccountingModeSwitch();
  const [th, setTh] = useState(given || null);
  const load = useCallback(() => {
    if (given || !company?._id || company.accounting_mode !== "simple") return;
    apiClient.get(`/companies/${company._id}/simple-ledger/summary`).then((r) => setTh(r.data.threshold)).catch(() => {});
  }, [company?._id, company?.accounting_mode, given]);
  useEffect(load, [load]);
  useEffect(() => { if (given) setTh(given); }, [given]);

  if (company?.accounting_mode !== "simple" || !th || th.status === "ok") return null;
  const exceeded = th.status === "exceeded";
  return (
    <Card className={`p-4 border ${exceeded ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"}`} data-testid="simple-mode-threshold-banner">
      <div className="flex items-start gap-3 flex-wrap">
        <AlertTriangle className={`h-5 w-5 shrink-0 mt-0.5 ${exceeded ? "text-rose-700" : "text-amber-700"}`} />
        <div className="flex-1 min-w-[240px] text-sm">
          <div className={`font-semibold ${exceeded ? "text-rose-900" : "text-amber-900"}`} data-testid="simple-mode-threshold-title">
            {exceeded ? "Umsatzschwelle CHF 500'000 überschritten" : `Umsatz bei ${Math.round(th.ratio * 100)} % der CHF 500'000-Schwelle`}
          </div>
          <p className={`mt-0.5 ${exceeded ? "text-rose-800" : "text-amber-800"}`}>
            Umsatz {th.year ? th.year : "dieses Jahr"}: <b>{chf(th.revenue)}</b>.{" "}
            {exceeded
              ? "Ab dem Folgejahr ist die doppelte Buchhaltung mit Bilanz und Erfolgsrechnung Pflicht (OR 957 Abs. 1). Die Umstellung ist ein Klick – alle Daten bleiben erhalten."
              : "Beim Erreichen von CHF 500'000 wird die doppelte Buchhaltung Pflicht. Du kannst jederzeit umstellen – ohne Datenverlust."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate("/simple-ledger")} data-testid="simple-mode-threshold-details">Details</Button>
          <Button size="sm" className="bg-slate-900 text-white" disabled={busy} onClick={() => switchMode("double")} data-testid="simple-mode-threshold-switch">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-1" />}Auf doppelte Buchhaltung umstellen
          </Button>
        </div>
      </div>
    </Card>
  );
}
